// The rollout action outside the Variables window (feature spec 15 19.3): the resources table's ⋯ menu and the
// inspector's Variables card. Texts come from the delivery registry; confirmed only where `nowAction.confirm` is true.
import { RefreshCw } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { Deployment } from '../api/types';
import { usePermission } from '../auth/context';
import { ActionButton } from '../components/ui';
import { ConfirmDialog } from '../deployments/runDialogs';
import type { MenuEntry } from '../shell/Menu';
import { strings } from '../strings';
import { useSecretActions } from './actions';
import { appDelivery, rollingOutSince } from './appStatusModel';
import type { VariablesData } from './useVariablesData';
import { isRollingOut } from './useVariablesData';

interface Rollout {
  label: string;
  busy: boolean;
  disabledReason: string | null;
  run: () => void;
}

export function useAppRollout(
  deployment: Deployment | null,
  variables: Pick<VariablesData, 'apps' | 'rows'>,
) {
  const actions = useSecretActions(deployment?.id ?? null);
  const canOperate = usePermission('operate');
  const [confirm, setConfirm] = useState<{ app: string; label: string; disruption: string } | null>(null);

  const start = (resourceId: string, label: string) => {
    const found = appDelivery({ resourceId }, variables.rows);
    void actions.restartApp(resourceId, {
      done: found?.entry.nowActionDoneText?.([resourceId]) ?? label,
      failed: (error) => found?.entry.nowActionFailedText?.(resourceId, error) ?? error,
    });
  };

  const rollout = (resourceId: string): Rollout | null => {
    const app = variables.apps.find((a) => a.resourceId === resourceId);
    if (!deployment || !app?.applied) return null;
    const found = appDelivery(app, variables.rows);
    if (!found) return null;
    const now = found.entry.nowAction([resourceId], found.delivery);
    if (!now) return null;
    const label = found.entry.nowActionShortLabel?.(resourceId) ?? now.label;
    const confirmed = found.entry.nowActionConfirm ?? found.delivery.nowAction?.confirm ?? false;
    const busy = actions.isRestarting(resourceId);
    return {
      label,
      busy,
      disabledReason: deployment.activeRunId
        ? strings.vaults.rolloutBusy
        : isRollingOut(app)
          ? rollingOutSince(app)
          : busy
            ? strings.common.loading
            : null,
      run: () =>
        confirmed
          ? setConfirm({ app: resourceId, label, disruption: now.disruption })
          : start(resourceId, label),
    };
  };

  /** The ⋯ menu entry (next to Refresh references, or instead of it). */
  const menuEntry = (resourceId: string): MenuEntry | null => {
    const r = rollout(resourceId);
    return r
      ? {
          kind: 'action',
          id: 'rollout',
          label: r.label,
          disabledReason: !canOperate
            ? strings.common.requiresRole('operator')
            : (r.disabledReason ?? undefined),
          onSelect: r.run,
        }
      : null;
  };

  const button = (resourceId: string): ReactNode => {
    const r = rollout(resourceId);
    return r ? (
      <ActionButton
        permission="operate"
        className="btn-sm"
        data-testid={`rollout-${resourceId}`}
        disabledReason={r.disabledReason}
        icon={<RefreshCw aria-hidden className={`size-3.5 ${r.busy ? 'animate-spin' : ''}`} />}
        onClick={r.run}
      >
        {r.label}
      </ActionButton>
    ) : null;
  };

  const dialog = confirm ? (
    <ConfirmDialog
      title={strings.vaults.confirmRestart(confirm.app)}
      message={confirm.disruption}
      confirmLabel={strings.vaults.confirmRestart(confirm.app)}
      busy={actions.isRestarting(confirm.app)}
      onClose={() => setConfirm(null)}
      onConfirm={() => {
        const target = confirm;
        setConfirm(null);
        start(target.app, target.label);
      }}
    />
  ) : null;

  return { menuEntry, button, dialog };
}
